// janus-engine/zones/walk-zone-crossings.ts — browser zone sweeps on paid movement feet (3D feet for traversals), or teleport endpoints.
// The legacy loop (move_encounter_creature_logic.php) runs, per foot step:
//   ENTRY  janus_try_persistent_aoe_entry_for_creature() for every zone the mover newly overlaps, and — when the mover carries
//          an aura — for every creature its aura newly covers (dice, damage, statuses, halts: Tier 1, S2);
// and at the END of the move:
//   EXIT   remove_creature_statuses_tied_to_exited_persistent_aoe_zone() for every creature and zone in (initial ∪ entered) −
//          final, then reconcile_persistent_aoe_exits_for_encounter() — the level-triggered sweep that strips, for EVERY
//          creature, the zone-delivered rows of every zone it is not standing in (connection-scoped, Concentrating kept),
//          after refreshing each class-aura carrier's stored radius.
// This resolver answers, conservatively:
//   { kind: 'clear' }                — no entry can fire and nothing is stripped: the walk is an ordinary walk;
//   { kind: 'exits', strips }        — no entry can fire; the end-of-move sweeps strip exactly these rows;
//   { kind: 'blocked', step, message } — a solid painted wall stops the body before this foot;
//   { kind: 'handback', reason }     — anything it cannot prove (an entry without `entries`, a
//                                      side-view zone, a class aura whose radius it cannot compute…).
// ENTRY PROOF (Codex S3 review F1/F2): the membership test runs at the EXACT foot positions the legacy loop visits —
// ft_move_resample_polyline(route, 1.0), whose JS twin is ft3d-move-core.js resamplePolyline (parity-tested) — including the
// wall gate at each of them. Ordinary zones use the shared inclusive membership test, including exact boundary contact;
// painted walls retain their precision guard. An out → in transition between consecutive positions is an entry.
import { collectPersistentZones, intersectingZoneKeys, zoneContains, zoneWallBlocked, zoneShape, zoneIsPainted, paintedZonePhases, paintedZoneSnapshot, ZoneHandBack,
  type Zone } from './zone-membership.js';
import type { AoeOccupancy } from '../ability/aoe/expand-placed-aoe-targets.js';
import { resamplePolyline } from '../../../../ft3d/engine/ft3d-move-core.js';
import { clipTraversalRoute, spatialSamples, type Point3, type TraversalSample } from '../movement/traversal-3d.js';
import { sameTeamOrSelf, ZONE_GATE_SKIP_SAME_TEAM } from '../ability/team-skip-gate.js';
import { FORCE_WALL_ID, ICE_WALL_ID, WATER_WALL_ID, WIND_WALL_ID, paintedWallRestrictsMover, paintedWallBlocksMovement } from '../../shared/painted-wall-rules.js';
import { itemsCol } from '../../shared/array-types.js';
import { parseSurfaceGeometry } from '../../shared/surface-geometry.js';
import { isSelfStatusOrigin } from '../statuses/status-origin.js';
import { refreshClassAuraZones, statusCasterId, type WalkZoneReaders } from './class-aura-refresh.js';
export { classAuraExpectedStored, refreshClassAuraZones, statusCasterId, type WalkZoneReaders } from './class-aura-refresh.js';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const rows = (value: unknown): Row[] => Array.isArray(value) ? value.filter(v => record(v) !== null) as Row[] : [];
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const f = (value: unknown): number => { const n = Number(Array.isArray(value) ? value[0] : value); return Number.isFinite(n) ? n : 0; };
const EPS_FT = 1e-6;
/** Bound one exit verdict; entry-created overflow is batched through the shared ability lane. */
export const MAX_ZONE_STRIPS = 64;
const CONCENTRATING = 'E7FDB796-26DA-E0F5-8F88-C21FE01D923B';

/** collect_persistent_aoes() as every movement judge reads it: class auras at their refreshed radius. */
export function collectWalkZones(encounter: Row, read: WalkZoneReaders, lenient = false): Zone[] {
  return refreshClassAuraZones(collectPersistentZones(encounter, read), rows(encounter.encounter_creatures_arr), read, { lenient });
}
export interface ZoneStrip { statusId: string; targetId: string; zoneStatusId: string; connectedId: string }
/** MV2 S2 (built by MV4, 2026-09-28): one DISPATCHING entry the walk makes — the zone's anchor row, the legacy foot it happens at. */
export interface ZoneEntry {
  zoneStatusId: string; casterId: string; originId: string; connectedId: string;
  /** Index into zoneStepSamples(route, startZ), with the entry's horizontal position. */
  step: number; point: [number, number];
  /** A spatial traversal enters at this height; flat walks retain their existing wire shape. */
  elevation?: number;
  /** The zone's cast-time forced extreme (status_forced_roll_extreme: 1 max, -1 min, 0 none — Empowered Spell). */
  forcedExtreme?: number;
  /** The mover does not stand in this zone at the end of the walk: the end-of-move sweep strips what the entry applied. */
  exitsAfter?: boolean;
}
/** A creature the mover's CARRIED aura newly covers on the way (it stands still): that zone's entries, resolved where it stands (`at`). */
export interface ZoneCoverage { actorId: string; at: [number, number, number]; entries: ZoneEntry[] }
/** A group relocation can discover an entry both from the carrier and from its entrant. Dispatch each pair once per move. */
export function uniqueZoneEntrants(groups: ReadonlyArray<{ actorId: string; entries: readonly ZoneEntry[] }>, seen: Set<string>):
  Array<{ actorId: string; entries: ZoneEntry[] }> {
  return groups.map(group => ({ actorId: upper(group.actorId), entries: group.entries.filter(entry => {
    const key = `${upper(entry.zoneStatusId)}|${upper(group.actorId)}`;
    if (seen.has(key)) return false;
    seen.add(key); return true;
  }) })).filter(group => group.entries.length > 0);
}
/** Shared broad phase before measuring a target body: stored half-span + largest extent + ordinary body margin. */
const auraReach = (zone: Zone): number => { const s = zone.status;
  return Math.hypot(f(s.status_distance_point_x ?? s.status_origin_point_x) - f(s.status_origin_point_x), f(s.status_distance_point_y ?? s.status_origin_point_y) - f(s.status_origin_point_y)) / 2
    + Math.max(f(s.status_aoe_distance), f(s.status_aoe_distance_height), f(s.status_aoe_distance_depth), f(s.status_aoe_line_half_width)) + 20; };
/** Endpoint relocation: compare each target against the moving carrier's anchors before and after the jump. */
export function relocationAuraCoverage(before: Row, after: Row, moverId: string, shape: Row | null, read: WalkZoneReaders,
  oldShape: Row | null, oldRead: WalkZoneReaders): ZoneCoverage[] {
  const creatures = rows(after.encounter_creatures_arr), previous = rows(before.encounter_creatures_arr);
  const zones = collectWalkZones(after, read).filter(z => upper(z.holderId) === upper(moverId));
  const oldZones = collectWalkZones(before, oldRead).filter(z => upper(z.holderId) === upper(moverId));
  const covered: ZoneCoverage[] = [];
  for (const creature of creatures) {
    const id = entityId(creature);
    if (!id || id === upper(moverId) || f(record(creature.encounter_creatures)?.encounter_creature_dead) === 1) continue;
    const at = read.position(creature);
    const eligible = zones.filter(z => z.dynamic && Math.hypot(z.dynamic[0] - at[0], z.dynamic[1] - at[1]) <= auraReach(z)
      && entryDispatches(z, creature, creatures, read));
    if (!eligible.length) continue;
    const now = new Set(intersectingZoneKeys(creature, eligible, shape, read));
    if (!now.size) continue;
    const old = previous.find(c => entityId(c) === id);
    const was = new Set(old ? intersectingZoneKeys(old, oldZones, oldShape, oldRead) : []);
    const entries = eligible.filter(z => now.has(z.key) && !was.has(z.key)).map(z => ({ zoneStatusId: upper(z.status.status_id),
      casterId: statusCasterId(z.status), originId: upper(z.status.status_origin_id || z.status.status_item_id), connectedId: upper(z.status.status_connected_id),
      forcedExtreme: Math.sign(f(z.status.status_forced_roll_extreme)), step: 0, point: [at[0], at[1]] as [number, number], exitsAfter: false }));
    if (entries.length) covered.push({ actorId: id, at, entries });
  }
  return covered;
}
export type WalkZoneResolution = { kind: 'clear' } | { kind: 'exits'; strips: ZoneStrip[] } | { kind: 'handback'; reason: string; detail?: Row }
  | { kind: 'blocked'; reason: 'painted-wall'; step: number; message: string }
  /** Only with `entries: true`: the walk makes these dispatching entries (in step order); `strips` = its own end-of-move strips, committed
   *  AFTER the entries (with each walk-through entry's rows). `covered` = the creatures its carried aura newly covers (`entries` may then be
   *  empty: only the aura entered anyone). */
  | { kind: 'entries'; entries: ZoneEntry[]; strips: ZoneStrip[]; covered?: ZoneCoverage[] };

const entityId = (row: Row): string => upper(record(row.encounter_creatures)?.encounter_creature_id);
const targetStatuses = (row: Row): Row[] => rows(record(row.statuses)?.target);
const shifted = (o: AoeOccupancy, x: number, y: number): AoeOccupancy => ({ ...o, x, y });
const bodyRadius = (o: AoeOccupancy): number => o.kind === 'oblong' ? Math.max(Number(o.halfW ?? 0), Number(o.halfD ?? 0)) : Number(o.r ?? 0);

/** The legacy loop's foot positions: index 0 is the start (never stepped), the last is the exact end. */
export function legacyStepPositions(route: ReadonlyArray<readonly number[]>): Array<[number, number]> {
  return (resamplePolyline(route.map(p => [Number(p[0]), Number(p[1])] as [number, number]), 1) as number[][]).map(p => [p[0]!, p[1]!]);
}

/** One sampler for classification, budget prefixes and halts. Flat walks retain their legacy feet; traversals carry z and segment identity. */
export function zoneStepSamples(route: ReadonlyArray<readonly number[]>, startZ: number, teleport = false): TraversalSample[] {
  const point = (p: readonly number[], seg: number): TraversalSample => ({ x: Number(p[0]), y: Number(p[1]), z: Number(p[2] ?? startZ), seg });
  if (teleport) return [point(route[0]!, 0), point(route[route.length - 1]!, route.length - 1)];
  if (route.some(p => p[2] !== undefined && Math.abs(Number(p[2]) - startZ) > 1e-9))
    return spatialSamples(route.map(p => [Number(p[0]), Number(p[1]), Number(p[2] ?? startZ)]));
  const feet = legacyStepPositions(route);
  let seg = 1, walked = 0;
  return feet.map((p, i) => {
    const progress = i === feet.length - 1 ? Infinity : i;
    while (seg < route.length - 1) {
      const a = route[seg - 1]!, b = route[seg]!;
      const length = Math.hypot(b[0]! - a[0]!, b[1]! - a[1]!);
      if (walked + length >= progress - 1e-9) break;
      walked += length; seg++;
    }
    return { x: p[0], y: p[1], z: startZ, seg: i === 0 ? 0 : seg };
  });
}

/** Split at a classifier foot, keeping bends, repeated positions and the foot's elevation on both sides. */
export function cutZoneRouteAtStep(route: ReadonlyArray<readonly number[]>, index: number, startZ: number):
  { route: number[][]; rest: number[][]; stop: Point3 } | null {
  const sample = zoneStepSamples(route, startZ)[index];
  if (!sample) return null;
  const polyline: Point3[] = route.map(p => [Number(p[0]), Number(p[1]), Number(p[2] ?? startZ)]);
  const stop: Point3 = [sample.x, sample.y, sample.z];
  const dimensions = polyline.some(p => Math.abs(p[2] - startZ) > 1e-9) ? 3 : 2;
  return { stop, route: clipTraversalRoute(polyline, sample, stop).map(p => p.slice(0, dimensions)),
    rest: [stop, ...polyline.slice(Math.max(1, sample.seg))].map(p => p.slice(0, dimensions)) };
}

const TEAM_FRIEND = '7DBEB6D4-D147-703A-022E-05B290BB95C3', TEAM_ENEMY = '9399856B-2132-7574-1684-91139A2831DD';
const truthy = (v: unknown): boolean => { const x = Array.isArray(v) ? v[0] : v; return x !== undefined && x !== null && x !== '' && x !== 0 && x !== '0' && x !== false; };
/** encounter_row_team_uuid(): the row's team column, else its `team` layer. */
function teamOf(row: Row, read: WalkZoneReaders): string | null {
  const t = record(row.encounter_creatures)?.team ?? row.team ?? read.layer(row, 'team');
  const v = Array.isArray(t) ? t[0] : t;
  return v === undefined || v === null || v === '' || typeof v === 'object' ? null : upper(v);
}
/** check_opposing_team(): both on friend / enemy, and different. */
function opposing(a: Row, b: Row, read: WalkZoneReaders): boolean {
  const ta = teamOf(a, read), tb = teamOf(b, read);
  if (!ta || !tb || ![TEAM_FRIEND, TEAM_ENEMY].includes(ta) || ![TEAM_FRIEND, TEAM_ENEMY].includes(tb)) return false;
  return ta !== tb;
}
/**
 * Would janus_try_persistent_aoe_entry_for_creature() DISPATCH for this entry? Its early returns that need no dice and no roll
 * log, in its order: the entering creature carries the anchor row itself; the origin is a terrain-only zone (pure
 * geometry); the team gate (status_opposing_team_only, effect_only_affects_opposing / _same_team_type) refuses. Anything
 * else — including the hostile campaign-time throttle, which reads the roll log — may dispatch: the caller hands back.
 */
export function entryDispatches(zone: Zone, entering: Row, creatures: Row[], read: WalkZoneReaders): boolean {
  const sid = upper(zone.status.status_id);
  if (targetStatuses(entering).some(s => upper(s.status_id) === sid)) return false;
  if (isSelfStatusOrigin(sid, upper(zone.status.status_origin_id))) return false;
  const origin = upper(zone.status.status_origin_id || zone.status.status_item_id);
  const def = read.definition(origin);
  if (!def) throw new ZoneHandBack('persistent-zone:entry-origin');
  if (String(record(def.items)?.item_type ?? def.item_type ?? '') === 'terrain_type') return false;
  if (record(def.attack_ability_arr) || read.has(def, 'attack_ability_arr')) throw new ZoneHandBack('persistent-zone:entry-rider');
  const casterId = statusCasterId(zone.status);
  const caster = casterId === '' ? entering : creatures.find(c => entityId(c) === casterId);
  if (!caster) throw new ZoneHandBack('persistent-zone:entry-caster-not-held');
  const isOpposing = opposing(caster, entering, read);
  // status_opposing_team_only = 2 (Wizard v9 aoe_spells_spare_allies): the zone skips its caster and the caster's team, never neutrals
  if (Number(zone.status.status_opposing_team_only ?? 0) === ZONE_GATE_SKIP_SAME_TEAM) {
    if (sameTeamOrSelf(caster, entering, (row, key) => read.layer(row, key))) return false;
  } else if (truthy(zone.status.status_opposing_team_only) && !isOpposing) return false;
  if (truthy(read.layer(def, 'effect_only_affects_opposing_team_type')) && !isOpposing) return false;
  if (truthy(read.layer(def, 'effect_only_affects_same_team_type')) && isOpposing) return false;
  return true;
}

/** Share ordinary-zone membership with the final sweep; retain painted-wall precision checks. */
function inside(zone: Zone, occupancy: AoeOccupancy, gate: () => boolean): boolean {
  if (!zoneIsPainted(zone)) return zoneContains(zone, occupancy) && !gate();
  const grown = zoneContains(zone, occupancy, EPS_FT), shrunk = zoneContains(zone, occupancy, -EPS_FT);
  if (grown !== shrunk) throw new ZoneHandBack('persistent-zone:boundary-precision');
  return grown && !gate();
}

export function resolveWalkZoneCrossings(input: {
  encounter: Row; mover: Row; route: ReadonlyArray<readonly number[]>; shape: Row | null; read: WalkZoneReaders;
  /** MV2 S2: report dispatching entries instead of handing them back (the caller commits them — websocket/zone-entry-truth.ts). */
  entries?: boolean;
  /** Creatures moving WITH this walk (a compound's members, standing at their landings): they keep their offset to the mover, so its
   *  carried aura neither enters nor leaves them on the way. */
  followers?: ReadonlySet<string>;
  /** Requested-route mover checks never sweep a carried aura over unpaid feet. */
  coverage?: boolean;
  /** Endpoint membership only: teleportation crosses no intervening space. */
  teleport?: boolean;
  /** Selected movement mode, never inferred from an available flying speed. */
  flying?: boolean;
}): WalkZoneResolution {
  const { encounter, mover, route, shape, read } = input;
  const entries: ZoneEntry[] = [];
  try {
    if (route.length < 2) return { kind: 'handback', reason: 'persistent-zone:route' };
    const moverId = entityId(mover);
    const zones = collectWalkZones(encounter, read);
    if (zones.length === 0) return { kind: 'clear' };
    const creatures = rows(encounter.encounter_creatures_arr);
    const moverStart = read.occupancy(mover);
    if (moverStart === undefined || moverStart === null) return { kind: 'handback', reason: 'persistent-zone:mover-occupancy' };
    const startZ = read.position(mover)[2];
    const nonFlat = route.some(p => p[2] !== undefined && Math.abs(Number(p[2]) - startZ) > 1e-9);
    const samples = zoneStepSamples(route, startZ, input.teleport);
    const steps: Array<[number, number]> = samples.map(p => [p.x, p.y]);
    const stepZ = (i: number): number => samples[i]!.z;
    if (steps.length === 0) return { kind: 'handback', reason: 'persistent-zone:route' };
    const [ex, ey] = steps[steps.length - 1]!, ez = stepZ(steps.length - 1);
    // A stationary reconcile uses its existing painted membership reader and never enters or collides.
    const stationary = steps.every((p, i) => Math.abs(p[0] - steps[0]![0]) < 1e-9 && Math.abs(p[1] - steps[0]![1]) < 1e-9 && Math.abs(stepZ(i) - stepZ(0)) < 1e-9);
    const paintedJudged = (zone: Zone): boolean => zoneIsPainted(zone) && stationary;
    for (const zone of zones) {
      if (!zoneIsPainted(zone)) { if (!parseSurfaceGeometry(zone.status.status_aoe_geometry)?.footprint) zoneShape(zone); }
      else if (!stationary && !paintedZoneSnapshot(zone)) throw new ZoneHandBack('persistent-zone:painted-geometry');
      if (!zone.status.status_connected_id) return { kind: 'handback', reason: 'persistent-zone:connectionless', detail: { zone: zone.key } };
      const def = read.definition(upper(zone.status.status_item_id));
      if (!def) return { kind: 'handback', reason: 'persistent-zone:definition-missing', detail: { zone: zone.key } };
      if (read.has(def, 'trap_trigger') || String(record(def.items)?.item_type ?? def.item_type ?? '') === 'trap_ability')
        return { kind: 'handback', reason: 'persistent-zone:trap', detail: { zone: zone.key } };
    }
    // Force/Ice/Wind use intact solid sections; Water blocks only frozen sections. Wind restricts gas and Tiny/Small flight.
    // ft_painted_wall_attachment() lists these exact catalog IDs; shared/painted-wall-rules owns their movement behavior.
    // Sweep each foot interval too: even a narrow body must not tunnel between samples. An initially overlapping body may depart.
    if (!stationary && !input.teleport && zones.some(zoneIsPainted)) {
      let blocked: Extract<WalkZoneResolution, { kind: 'blocked' }> | null = null;
      const volume = read.spatialVolume ? read.spatialVolume(mover) : moverStart;
      if (volume === undefined) throw new ZoneHandBack('persistent-zone:painted-volume');
      for (const zone of zones) {
        if (!zoneIsPainted(zone)) continue;
        const ability = upper(zone.status.status_origin_id || zone.status.status_item_id);
        const profile = ability === WIND_WALL_ID ? read.movementProfile?.(mover) : undefined;
        if (ability === WIND_WALL_ID && (!profile || typeof input.flying !== 'boolean'))
          throw new ZoneHandBack('persistent-zone:painted-wall');
        if (!paintedWallRestrictsMover(ability, profile ?? { sizeId: '', gaseous: false }, input.flying === true)) continue;
        const phases = paintedZonePhases(zone), walls = ability === WATER_WALL_ID ? phases.frozen : phases.solid;
        const name = String(itemsCol(isSelfStatusOrigin(upper(zone.status.status_id), ability) ? null : read.definition(ability), 'item_name') ??
          (ability === FORCE_WALL_ID ? 'Force Wall' : ability === ICE_WALL_ID ? 'Wall of Ice' : ability === WIND_WALL_ID ? 'Wind Wall' : 'Wall of Water'));
        for (let i = 1; i < (blocked?.step ?? steps.length); i++) {
          const from = samples[i - 1]!, to = samples[i]!;
          const z = Number(volume!.zMin) - startZ;
          if (walls.some(wall => paintedWallBlocksMovement(wall, { x: from.x, y: from.y, z: from.z + z },
            { x: to.x, y: to.y, z: to.z + z }, bodyRadius(volume!), Number(volume!.zMax) - Number(volume!.zMin)))) {
            blocked = { kind: 'blocked', reason: 'painted-wall', step: i, message: `${name} blocks this creature.` }; break;
          }
        }
      }
      if (blocked) return blocked;
    }
    // A carried aura moves with the mover: its centre is the carrier's feet (collect_persistent_aoes reads them live), so the walk's own commit
    // moves it. Every other creature it newly covers at a legacy foot gets that zone's entry — the same out → in test as the mover's own
    // entries, with the creature at its own feet and the aura centred on the mover's foot (owner bug 2026-10-15, Paladin Guy's Aura of Safety:
    // the old blanket hand-back, kept for the legacy sweep's wrong trajectory, became a refusal of every walk by an aura carrier). Creatures
    // moving with the walk keep their offset, and a corpse is not entered (no aura acts on it). The end-of-move sweep below strips whoever it
    // left behind.
    const carried = zones.filter(z => upper(z.holderId) === moverId);
    const covered: ZoneCoverage[] = [];
    for (const zone of carried) if (!zone.dynamic) return { kind: 'handback', reason: 'persistent-zone:carried-aura-geometry', detail: { zone: zone.key } };
    const auraAt = (zone: Zone, at: readonly [number, number], z: number): Zone => ({ ...zone, dynamic: [at[0], at[1], z] });
    // Only a walk newly covers anyone: a stationary judgement (a force move's landing, a reconcile) makes no entry.
    if (input.coverage !== false && carried.length && !stationary) for (const creature of creatures) {
      const id = entityId(creature);
      if (id === '' || id === moverId || input.followers?.has(id) || f(record(creature.encounter_creatures)?.encounter_creature_dead) === 1) continue;
      const [cx, cy, cz] = read.position(creature);
      // The entry gate first — it does not depend on where anyone stands: a zone that can never enter this creature (a same-team aura and an
      // enemy) never measures it. A gate the page cannot decide is kept, and decided only if the aura really reaches the creature.
      const candidates: Array<{ zone: Zone; gate: ZoneHandBack | null }> = [];
      for (const zone of carried) {
        if (!steps.some(p => Math.hypot(p[0] - cx, p[1] - cy) <= auraReach(zone))) continue;
        try { if (entryDispatches(zone, creature, creatures, read)) candidates.push({ zone, gate: null }); }
        catch (error) { if (!(error instanceof ZoneHandBack)) throw error; candidates.push({ zone, gate: error }); }
      }
      if (!candidates.length) continue;
      const body = read.occupancy(creature);
      if (body === undefined) return { kind: 'handback', reason: 'persistent-zone:occupancy-missing', detail: { creature: id } };
      if (body === null) continue;
      // Membership exactly as the end-of-move sweep judges it (intersectingZoneKeys: the shape test, then the wall gate) — the browser decides.
      const covers = (zone: Zone, i: number): boolean => { const here = auraAt(zone, steps[i]!, stepZ(i)); return zoneContains(here, body) && !zoneWallBlocked(here, shape, creature, read); };
      const own: ZoneEntry[] = [];
      for (const { zone, gate } of candidates) {
        let was = covers(zone, 0);
        for (let i = 1; i < steps.length; i++) {
          const now = covers(zone, i);
          if (now && !was) {
            if (gate) throw gate;
            if (!input.entries) return { kind: 'handback', reason: 'persistent-zone:aura-entry', detail: { zone: zone.key, creature: id, step: i } };
            own.push({ zoneStatusId: upper(zone.status.status_id), casterId: statusCasterId(zone.status),
              originId: upper(zone.status.status_origin_id || zone.status.status_item_id), connectedId: upper(zone.status.status_connected_id),
              step: i, point: [cx, cy], forcedExtreme: Math.sign(f(zone.status.status_forced_roll_extreme)) });
            break; // janus_try_persistent_aoe_entry_for_creature's per-move dedupe (status_id|creature): a re-entry within the move never fires
          }
          was = now;
        }
      }
      if (own.length) covered.push({ actorId: id, at: [cx, cy, cz], entries: own.sort((a, b) => a.step - b.step) });
    }
    // The mover against every zone it does not carry, at every legacy foot position (exact, wall gate included).
    for (const zone of zones) {
      if (carried.includes(zone) || input.followers?.has(upper(zone.holderId)) || paintedJudged(zone)) continue;
      const startBody = zoneIsPainted(zone) && read.spatialVolume ? read.spatialVolume(mover) : moverStart;
      if (!startBody) throw new ZoneHandBack('persistent-zone:painted-volume');
      let was = inside(zone, startBody, () => zoneWallBlocked(zone, shape, mover, read, steps[0]!));
      for (let i = 1; i < steps.length; i++) {
        const at = steps[i]!;
        const moved = { ...mover, position: { ...record(mover.position), x_pos: at[0], y_pos: at[1], z_pos: stepZ(i) } };
        const body = shifted(startBody, at[0], at[1]);
        if (nonFlat) { body.zMin = Number(startBody.zMin) + stepZ(i) - startZ; body.zMax = Number(startBody.zMax) + stepZ(i) - startZ; }
        const now = inside(zone, body, () => zoneWallBlocked(zone, shape, moved, read, at));
        // An entry the legacy helper provably answers without dispatching (team gate, terrain-only, own anchor) changes nothing.
        if (now && !was && entryDispatches(zone, mover, creatures, read)) {
          if (!input.entries) return { kind: 'handback', reason: 'persistent-zone:entry', detail: { zone: zone.key, creature: moverId, step: i } };
          // janus_try_persistent_aoe_entry_for_creature's per-move dedupe (status_id|creature): a re-entry within the move never fires.
          if (!entries.some(e => e.zoneStatusId === upper(zone.status.status_id))) {
            entries.push({ zoneStatusId: upper(zone.status.status_id), casterId: statusCasterId(zone.status),
              originId: upper(zone.status.status_origin_id || zone.status.status_item_id), connectedId: upper(zone.status.status_connected_id),
              step: i, point: [at[0], at[1]], ...(nonFlat ? { elevation: stepZ(i) } : {}), forcedExtreme: Math.sign(f(zone.status.status_forced_roll_extreme)) });
          }
        }
        was = now;
      }
    }
    // The end-of-move sweeps: every creature at its FINAL position against every zone as it stands at the end (a carried
    // aura centred on the mover's last foot). A creature not in a zone loses that zone's connection-scoped rows, Concentrating
    // and the anchor kept — the edge exit and the level-triggered reconcile (a superset of the edge exit) in one pass.
    const finalZones = zones.map(z => z.dynamic && upper(z.holderId) === moverId ? { ...z, dynamic: [ex, ey, ez] as [number, number, number] } : z);
    const finalBody = { ...shifted(moverStart, ex, ey), zMin: Number(moverStart.zMin) + ez - startZ, zMax: Number(moverStart.zMax) + ez - startZ };
    const finalMover = { ...mover, position: { ...record(mover.position), x_pos: ex, y_pos: ey, z_pos: ez } };
    const finalRead = { ...read, occupancy: () => finalBody, position: () => [ex, ey, ez] as [number, number, number],
      ...(read.spatialVolume ? { spatialVolume: (row: Row) => {
        const volume = read.spatialVolume!(row);
        return volume && { ...shifted(volume, ex, ey), zMin: Number(volume.zMin) + ez - startZ, zMax: Number(volume.zMax) + ez - startZ };
      } } : {}),
      rayZ: (_row: Row, x: number, y: number) => read.rayZ(finalMover, x, y) };
    const strips = new Map<string, ZoneStrip>(); // one strip per status row, however many outside zones share its connection
    for (const creature of creatures) {
      const id = entityId(creature);
      if (id === '') continue;
      const statuses = targetStatuses(creature);
      if (statuses.length === 0) continue;
      const judge: WalkZoneReaders = id === moverId
        ? finalRead
        : read;
      const insideKeys = new Set(intersectingZoneKeys(creature, finalZones, shape, judge));
      for (const zone of finalZones) {
        if (insideKeys.has(zone.key)) continue;
        const connected = upper(zone.status.status_connected_id);
        for (const s of statuses) {
          if (upper(s.status_connected_id) !== connected || upper(s.status_item_id) === CONCENTRATING) continue;
          const sid = upper(s.status_id);
          if (sid === '' || sid === upper(zone.status.status_id) || strips.has(sid)) continue; // never the anchor itself
          strips.set(sid, { statusId: sid, targetId: id, zoneStatusId: upper(zone.status.status_id), connectedId: connected });
        }
      }
    }
    if (strips.size > MAX_ZONE_STRIPS) return { kind: 'handback', reason: 'persistent-zone:strip-ceiling', detail: { strips: strips.size } };
    // A creature the aura passed over and left (not inside it at the end) loses what its entry gave it, like the mover's walk-through.
    for (const c of covered) {
      const creature = creatures.find(r => entityId(r) === c.actorId)!;
      const keys = new Set(intersectingZoneKeys(creature, finalZones, shape, read));
      for (const e of c.entries) {
        const zone = finalZones.find(z => upper(z.status.status_id) === e.zoneStatusId);
        if (!zone) return { kind: 'handback', reason: 'persistent-zone:entry-zone-not-final', detail: { zone: e.zoneStatusId } };
        e.exitsAfter = !keys.has(zone.key);
      }
    }
    if (covered.length && !entries.length) return { kind: 'entries', entries: [], strips: [...strips.values()], covered };
    if (entries.length) {
      // The legacy loop fires each entry mid-walk and runs the exit sweeps at the END. So the caller commits the walk WITHOUT its strips,
      // then the entries in foot order, then ONE exit commit: these strips plus — for an entry into a zone the mover no longer stands in
      // at the end (`exitsAfter`, a walk-through) — the rows that entry left on the zone's connection (second cut, 2026-09-28).
      const moverFinal = new Set(intersectingZoneKeys(mover, finalZones, shape,
        finalRead));
      for (const entry of entries) {
        const zone = finalZones.find(z => upper(z.status.status_id) === entry.zoneStatusId);
        if (!zone) return { kind: 'handback', reason: 'persistent-zone:entry-zone-not-final', detail: { zone: entry.zoneStatusId } };
        entry.exitsAfter = !moverFinal.has(zone.key);
      }
      // The legacy loop resolves entries foot by foot; one foot's entries in collect_persistent_aoes() order (the zones' order here — stable sort).
      return { kind: 'entries', entries: [...entries].sort((a, b) => a.step - b.step), strips: [...strips.values()], ...(covered.length ? { covered } : {}) };
    }
    return strips.size ? { kind: 'exits', strips: [...strips.values()] } : { kind: 'clear' };
  } catch (error) {
    if (error instanceof ZoneHandBack) return { kind: 'handback', reason: error.reason };
    throw error;
  }
}
